import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Paper,
  Dialog,
  Button,
} from '@mui/material';
import {
  Call as CallIcon,
  CallEnd as CallEndIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  CameraSwitch as CameraSwitchIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import WebRTCService from '../services/WebRTCService';
import CallSocketService from '../services/sub/CallSocketService';
import MessageService from '../services/MessageService';

const CallScreen = ({ overlayParams = null, onRequestClose = null } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => {
    return overlayParams || location.state || {};
  }, [overlayParams, location.state]);

  const {
    callId,
    recipientId,
    recipientName,
    recipientAvatar,
    conversationId,
    isIncoming = false,
    callType = 'audio',
  } = params;

  const [callState, setCallState] = useState(isIncoming ? 'incoming' : 'calling');
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaker, setIsSpeaker] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [connectionState, setConnectionState] = useState('new');
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [endModal, setEndModal] = useState({ open: false, reason: '', details: '' });
  const [videoStats, setVideoStats] = useState({ local: null, remote: null });
  const [localSpeaking, setLocalSpeaking] = useState(false);
  const [remoteSpeaking, setRemoteSpeaking] = useState(false);
  const [conversationInfo, setConversationInfo] = useState({
    name: recipientName,
    avatar: recipientAvatar,
  });

  const callTimerRef = useRef(null);
  const callIdRef = useRef(callId);
  
  // Debug: log callId initialization
  useEffect(() => {
    console.log('📞 CallScreen initialized. callId from params:', callId);
    console.log('📞 callIdRef.current:', callIdRef.current);
  }, []);
  const pendingIceCandidatesRef = useRef([]);
  const webrtcInitPromiseRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const isVideoCall = callType === 'video';
  const ringStartAtRef = useRef(Date.now());
  const connectedAtRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const callDurationRef = useRef(0);

  useEffect(() => {
    callDurationRef.current = callDuration;
  }, [callDuration]);

  const getAspectRatioText = (w, h) => {
    if (!w || !h) return '';
    const gcd = (a, b) => (b ? gcd(b, a % b) : a);
    const g = gcd(w, h);
    return `${Math.round(w / g)}:${Math.round(h / g)}`;
  };

  const startSpeakingMonitor = useCallback((stream, setSpeaking) => {
    if (!stream) return () => {
    };

    let rafId = null;
    let audioContext;
    let analyser;
    let source;
    let cancelled = false;
    let lastSpeakingAt = 0;

    const run = async () => {
      try {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024;
        source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        const data = new Uint8Array(analyser.fftSize);

        const tick = () => {
          if (cancelled) return;
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i += 1) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / data.length);
          const now = Date.now();
          const isSpeaking = rms > 0.03;
          if (isSpeaking) {
            lastSpeakingAt = now;
          }
          const hold = now - lastSpeakingAt < 250;
          setSpeaking(Boolean(isSpeaking || hold));
          rafId = window.requestAnimationFrame(tick);
        };

        rafId = window.requestAnimationFrame(tick);
      } catch {
      }
    };

    run();

    return () => {
      cancelled = true;
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      try {
        source?.disconnect();
      } catch {
      }
      try {
        analyser?.disconnect();
      } catch {
      }
      try {
        audioContext?.close();
      } catch {
      }
    };
  }, []);

  useEffect(() => {
    if (callState !== 'connected') return;
    if (isVideoCall) return;

    const stopLocal = localStream ? startSpeakingMonitor(localStream, setLocalSpeaking) : null;
    const stopRemote = remoteStream ? startSpeakingMonitor(remoteStream, setRemoteSpeaking) : null;

    return () => {
      if (stopLocal) stopLocal();
      if (stopRemote) stopRemote();
    };
  }, [callState, isVideoCall, localStream, remoteStream, startSpeakingMonitor]);

  // Check if media permissions are granted
  const checkMediaPermissions = async () => {
    try {
      const permissions = await navigator.permissions.query({ name: 'microphone' });
      if (isVideoCall) {
        const cameraPermissions = await navigator.permissions.query({ name: 'camera' });
        return permissions.state === 'granted' && cameraPermissions.state === 'granted';
      }
      return permissions.state === 'granted';
    } catch (err) {
      console.error('Permission check error:', err);
      return false;
    }
  };

  // Request audio/video permissions
  const requestMediaPermissions = async () => {
    try {
      const constraints = {
        audio: true,
        video: isVideoCall,
      };
      const testStream = await navigator.mediaDevices.getUserMedia(constraints);
      // Stop permission-check stream immediately; WebRTCService will acquire its own stream.
      testStream.getTracks().forEach((t) => t.stop());
      return true;
    } catch (error) {
      console.error('❌ Permission denied:', error);
      alert(`${isVideoCall ? 'Camera and microphone' : 'Microphone'} access is required to make ${isVideoCall ? 'video' : 'voice'} calls.`);
      return false;
    }
  };

  const closeEndModal = useCallback(() => {
    setEndModal({ open: false, reason: '', details: '' });
    if (typeof onRequestClose === 'function') {
      onRequestClose();
      return;
    }
    navigate(-1);
  }, [navigate, onRequestClose]);

  const showEndModal = useCallback((reason, details = '') => {
    stopCallTimer();
    WebRTCService.endCall();
    setEndModal({ open: true, reason, details });
    
    // Auto-close the call after showing summary for 3 seconds
    setTimeout(() => {
      closeEndModal();
    }, 3000);
  }, [closeEndModal]);

  const closeOrBack = useCallback(() => {
    if (typeof onRequestClose === 'function') {
      onRequestClose();
      return;
    }
    navigate(-1);
  }, [navigate, onRequestClose]);

  // Initialize WebRTC
  const initializeWebRTC = useCallback(async () => {
    try {
      // console.log(`🎬 Initializing WebRTC for ${isVideoCall ? 'video' : 'audio'} call...`);
      
      // Check and request permissions
      const hasPermission = await requestMediaPermissions();
      if (!hasPermission) {
        console.log('❌ Cannot proceed without required permissions');
        endCall();
        return;
      }

      // Initialize peer connection
      await WebRTCService.initializePeerConnection();

      // Get local stream (audio or audio+video)
      const stream = await WebRTCService.getLocalStream(isVideoCall);
      // console.log(isVideoCall ? '📹 Local video stream obtained' : '🎤 Local audio stream obtained');
      setLocalStream(stream);

      // Set local video element
      if (localVideoRef.current && isVideoCall) {
        localVideoRef.current.srcObject = stream;
      }

      // Add stream to peer connection
      WebRTCService.addLocalStreamToPeer();

      // Set up callbacks
      WebRTCService.setOnRemoteStream((remoteStream) => {
        // console.log('📺 Remote stream received in CallScreen');
        setRemoteStream(remoteStream);

        if (!connectedAtRef.current) {
          connectedAtRef.current = Date.now();
        }
        
        // Set remote video element
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteStream;
          Promise.resolve(remoteVideoRef.current.play()).catch(() => {
          });
        }

        // Ensure audio plays (some browsers block audio on <video> autoplay)
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = remoteStream;
          Promise.resolve(remoteAudioRef.current.play()).catch(() => {
          });
        }
        
        setCallState('connected');
        startCallTimer();
      });

      WebRTCService.setOnIceCandidate((candidate) => {
        const currentCallId = callIdRef.current;
        if (!currentCallId) {
          pendingIceCandidatesRef.current.push(candidate);
          return;
        }
        CallSocketService.sendIceCandidate(currentCallId, candidate);
      });

      WebRTCService.setOnConnectionStateChange((state) => {
        // console.log('🔗 Connection state changed:', state);
        setConnectionState(state);
        
        if (state === 'connected') {
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
          }
          setCallState('connected');
          startCallTimer();
        } else if (state === 'disconnected') {
          setCallState('connecting');
          if (!reconnectTimerRef.current) {
            reconnectTimerRef.current = setTimeout(() => {
              reconnectTimerRef.current = null;
              const ringSeconds = Math.max(0, Math.floor((Date.now() - ringStartAtRef.current) / 1000));
              const details = connectedAtRef.current
                ? `Call duration: ${formatDuration(callDuration)}`
                : `Ringing duration: ${formatDuration(ringSeconds)}`;
              showEndModal('Network error', details);
            }, 7000);
          }
        } else if (state === 'failed' || state === 'closed') {
          const ringSeconds = Math.max(0, Math.floor((Date.now() - ringStartAtRef.current) / 1000));
          const details = connectedAtRef.current
            ? `Call duration: ${formatDuration(callDuration)}`
            : `Ringing duration: ${formatDuration(ringSeconds)}`;
          showEndModal('Network error', details);
        }
      });

      // console.log('✅ WebRTC initialized successfully');
    } catch (error) {
      console.error('❌ Error initializing WebRTC:', error);
      alert('Failed to initialize call. Please try again.');
      endCall();
    }
  }, [isVideoCall]);

  const ensureWebRTCInitialized = useCallback(async () => {
    if (!webrtcInitPromiseRef.current) {
      webrtcInitPromiseRef.current = initializeWebRTC();
    }
    return webrtcInitPromiseRef.current;
  }, [initializeWebRTC]);

  useEffect(() => {
    if (!isVideoCall) return;
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
      Promise.resolve(localVideoRef.current.play()).catch(() => {
      });
    }
  }, [isVideoCall, localStream]);

  useEffect(() => {
    if (remoteStream) {
      if (remoteVideoRef.current && isVideoCall) {
        remoteVideoRef.current.srcObject = remoteStream;
        Promise.resolve(remoteVideoRef.current.play()).catch(() => {
        });
      }
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = remoteStream;
        Promise.resolve(remoteAudioRef.current.play()).catch(() => {
        });
      }
    }
  }, [isVideoCall, remoteStream]);

  // Start call timer
  const startCallTimer = () => {
    if (callTimerRef.current) return;
    
    callTimerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  };

  // Stop call timer
  const stopCallTimer = () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
  };

  // Format call duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Accept incoming call
  const acceptCall = async () => {
    // console.log('✅ Accepting call...');
    
    // Check permission before accepting
    const hasPermission = await requestMediaPermissions();
    if (!hasPermission) {
      // console.log('❌ Cannot accept call without required permissions');
      rejectCall();
      return;
    }
    
    setCallState('connecting');
    await initializeWebRTC();
    CallSocketService.acceptCall(callIdRef.current);
  };

  // Reject incoming call
  const rejectCall = () => {
    // console.log('❌ Rejecting call...');
    CallSocketService.rejectCall(callIdRef.current);
    closeOrBack();
  };

  // End call
  const endCall = useCallback(() => {
    console.log('📞 endCall() triggered. callId:', callIdRef.current);
    stopCallTimer();
    CallSocketService.endCall(callIdRef.current);
    const ringSeconds = Math.max(0, Math.floor((Date.now() - ringStartAtRef.current) / 1000));
    const details = connectedAtRef.current
      ? `Call duration: ${formatDuration(callDurationRef.current)}`
      : `Ringing duration: ${formatDuration(ringSeconds)}`;
    showEndModal('You ended the call', details);
  }, [showEndModal]);

  // Toggle mute
  const toggleMute = () => {
    const muted = WebRTCService.toggleMute();
    setIsMuted(muted);
  };

  // Toggle video (for video calls)
  const toggleVideo = () => {
    if (isVideoCall) {
      const videoDisabled = WebRTCService.toggleVideo();
      setIsVideoEnabled(!videoDisabled);
    }
  };

  // Toggle speaker
  const toggleSpeaker = () => {
    setIsSpeaker(!isSpeaker);
    // Web browsers handle speaker output automatically
  };

  // Set up socket listeners
  useEffect(() => {
    console.log('🔌 Setting up call socket listeners...');

    // Listen for call accepted
    const unsubAccepted = CallSocketService.onCallAccepted(async (data) => {
      console.log('✅ Call accepted event received:', data);
      setCallState('connecting');

      await ensureWebRTCInitialized();
      
      // Create and send offer
      const offer = await WebRTCService.createOffer();
      CallSocketService.sendOffer(callIdRef.current, offer);
    });

    // Listen for call rejected
    const unsubRejected = CallSocketService.onCallRejected((data) => {
      console.log('❌ Call rejected event received:', data);
      // Only process if this event is for our call (or no callId specified for backward compatibility)
      if (data.callId && data.callId !== callIdRef.current) {
        console.log('Ignoring call:rejected for different call:', data.callId, 'our call:', callIdRef.current);
        return;
      }
      const ringSeconds = Math.max(0, Math.floor((Date.now() - ringStartAtRef.current) / 1000));
      showEndModal('Call rejected', `Ringing duration: ${formatDuration(ringSeconds)}`);
    });

    // Listen for call ended
    const unsubEnded = CallSocketService.onCallEnded((data) => {
      console.log('📴 Call ended event received:', data);
      // Only process if this event is for our call
      if (data.callId && data.callId !== callIdRef.current) {
        console.log('Ignoring call:end for different call:', data.callId, 'our call:', callIdRef.current);
        return;
      }
      const ringSeconds = Math.max(0, Math.floor((Date.now() - ringStartAtRef.current) / 1000));
      const currentDuration = callDurationRef.current;
      const details = connectedAtRef.current
        ? `Call duration: ${formatDuration(currentDuration)}`
        : `Ringing duration: ${formatDuration(ringSeconds)}`;
      showEndModal('User ended the call', details);
    });

    // Listen for WebRTC offer
    const unsubOffer = CallSocketService.onOffer(async (data) => {
      await ensureWebRTCInitialized();
      await WebRTCService.setRemoteDescription(data.offer);
      
      // Create and send answer
      const answer = await WebRTCService.createAnswer();
      CallSocketService.sendAnswer(callIdRef.current, answer);
    });

    // Listen for WebRTC answer
    const unsubAnswer = CallSocketService.onAnswer(async (data) => {
      await WebRTCService.setRemoteDescription(data.answer);
    });

    // Listen for ICE candidates
    const unsubIce = CallSocketService.onIceCandidate(async (data) => {
      await WebRTCService.addIceCandidate(data.candidate);
    });

    return () => {
      if (typeof unsubAccepted === 'function') unsubAccepted();
      if (typeof unsubRejected === 'function') unsubRejected();
      if (typeof unsubEnded === 'function') unsubEnded();
      if (typeof unsubOffer === 'function') unsubOffer();
      if (typeof unsubAnswer === 'function') unsubAnswer();
      if (typeof unsubIce === 'function') unsubIce();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callInitiatedRef = React.useRef(false);

  // Initialize call for outgoing OR auto-accept for incoming
  useEffect(() => {
    let isMounted = true;
    
    const initCall = async () => {
      if (callInitiatedRef.current) return;
      callInitiatedRef.current = true;
      // Connect to call socket service first
      try {
        await CallSocketService.connect();
      } catch (error) {
        console.error('❌ Failed to connect to CallSocketService:', error);
        if (isMounted) {
          alert('Failed to connect to call service. Please try again.');
          closeOrBack();
        }
        return;
      }

      if (!isIncoming) {
        // Initiate call FIRST and get the callId from server (required for signaling)
        let newCallId;
        try {
          newCallId = await CallSocketService.initiateCall(recipientId, conversationId, callType);
        } catch (err) {
          console.error('❌ Failed to initiate call:', err);
          if (isMounted) {
            alert('Failed to connect call. Please try again.');
            closeOrBack();
          }
          return;
        }
        if (newCallId) {
          callIdRef.current = newCallId;

          // Flush any ICE candidates that may have been generated early
          if (pendingIceCandidatesRef.current.length > 0) {
            pendingIceCandidatesRef.current.forEach((c) => {
              CallSocketService.sendIceCandidate(newCallId, c);
            });
            pendingIceCandidatesRef.current = [];
          }
        }

        // Then initialize WebRTC to start media + candidate generation
        await ensureWebRTCInitialized();
      } else {
        // For incoming calls, auto-accept since user already accepted in the modal
        
        const doAccept = async () => {
          const hasPermission = await requestMediaPermissions();
          if (!hasPermission) {
            console.log('❌ Cannot accept call without required permissions');
            if (isMounted) closeOrBack();
            return;
          }
          
          setCallState('connecting');
          await ensureWebRTCInitialized();
          CallSocketService.acceptCall(callIdRef.current);
        };
        
        doAccept();
      }
    };
    
    initCall();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIncoming, recipientId, conversationId]);

  // Fetch conversation info
  useEffect(() => {
    const fetchConversationInfo = async () => {
      if (conversationId) {
        try {
          const conversation = await MessageService.getConversation(conversationId);
          setConversationInfo({
            name: conversation.name || recipientName,
            avatar: conversation.avatar || recipientAvatar,
          });
        } catch (error) {
          console.error('❌ Failed to fetch conversation for call:', error);
        }
      }
    };
    
    fetchConversationInfo();
  }, [conversationId, recipientName, recipientAvatar]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCallTimer();
      WebRTCService.endCall();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, []);

  // Render call state text
  const getCallStateText = () => {
    switch (callState) {
      case 'incoming':
        return 'Incoming call...';
      case 'calling':
        return 'Calling...';
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return formatDuration(callDuration);
      default:
        return 'Call';
    }
  };

  const isOverlayMode = Boolean(overlayParams);

  return (
    <Box
      sx={{
        position: isOverlayMode ? 'relative' : 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        zIndex: isOverlayMode ? 'auto' : 1200,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {isVideoCall ? 'Video Call' : 'Audio Call'}
        </Typography>
      </Box>

      {isVideoCall ? (
        /* Video Call UI */
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            pb: 14,
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 1100,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Paper
              elevation={10}
              sx={{
                flex: 2,
                backgroundColor: '#000',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
                height: { xs: 260, sm: 360, md: 440 },
                minHeight: { xs: 220, sm: 320, md: 400 },
                position: 'relative',
              }}
            >
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                muted
                onLoadedMetadata={(e) => {
                  const w = e.currentTarget.videoWidth;
                  const h = e.currentTarget.videoHeight;
                  if (w && h) {
                    setVideoStats((prev) => ({
                      ...prev,
                      remote: { width: w, height: h, ar: getAspectRatioText(w, h) },
                    }));
                  }
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#000',
                }}
              />

              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Typography variant="subtitle1" color="text.primary" fontWeight="600">
                  {conversationInfo?.name || recipientName}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {getCallStateText()}
                </Typography>
                {videoStats?.remote?.width ? (
                  <Typography variant="caption" color="text.primary" sx={{ display: 'block' }}>
                    {`Remote: ${videoStats.remote.width}x${videoStats.remote.height}${videoStats.remote.ar ? ` (${videoStats.remote.ar})` : ''}`}
                  </Typography>
                ) : null}
              </Box>
            </Paper>

            <Paper
              elevation={6}
              sx={{
                flex: 1,
                backgroundColor: '#000',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
                height: { xs: 200, sm: 260, md: 320 },
                minHeight: { xs: 160, sm: 220, md: 280 },
              }}
            >
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                onLoadedMetadata={(e) => {
                  const w = e.currentTarget.videoWidth;
                  const h = e.currentTarget.videoHeight;
                  if (w && h) {
                    setVideoStats((prev) => ({
                      ...prev,
                      local: { width: w, height: h, ar: getAspectRatioText(w, h) },
                    }));
                  }
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#000',
                  transform: 'scaleX(-1)',
                }}
              />
            </Paper>
          </Box>
        </Box>
      ) : (
        /* Audio Call UI or Connecting State */
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: 4,
            pb: 14,
          }}
        >
          <Box
            sx={{
              borderRadius: '50%',
              p: 0.5,
              mb: 4,
              backgroundColor: localSpeaking ? 'rgba(76, 175, 80, 0.2)' : remoteSpeaking ? 'rgba(33, 150, 243, 0.2)' : 'transparent',
              boxShadow: localSpeaking
                ? '0 0 0 8px rgba(76, 175, 80, 0.1)'
                : remoteSpeaking
                  ? '0 0 0 8px rgba(33, 150, 243, 0.1)'
                  : 'none',
              transition: 'all 150ms ease',
            }}
          >
            <Avatar
              src={conversationInfo?.avatar || recipientAvatar}
              sx={{
                width: 140,
                height: 140,
                border: '3px solid',
                borderColor: localSpeaking ? 'success.main' : remoteSpeaking ? 'info.main' : 'divider',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              }}
              imgProps={{
                onError: () => {
                  setConversationInfo((prev) => ({
                    name: prev?.name,
                    avatar: recipientAvatar || null,
                  }));
                },
              }}
            >
              <Typography variant="h2">
                {(conversationInfo?.name || recipientName).charAt(0).toUpperCase()}
              </Typography>
            </Avatar>
          </Box>
          
          <Typography variant="h4" color="text.primary" fontWeight="600" gutterBottom>
            {conversationInfo?.name || recipientName}
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {getCallStateText()}
          </Typography>
          
          {connectionState !== 'connected' && callState === 'connecting' && (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Establishing connection...
            </Typography>
          )}
        </Box>
      )}

      <audio ref={remoteAudioRef} autoPlay playsInline />

      {/* Call Controls */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          px: { xs: 2, sm: 3, md: 4 },
          pb: { xs: 3, sm: 4, md: 5 },
          pt: { xs: 2, sm: 3 },
          backgroundColor: isOverlayMode ? 'rgba(255, 255, 255, 0.98)' : 'rgba(0, 0, 0, 0.75)',
        }}
      >
        {callState === 'incoming' ? (
          // Incoming call buttons
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 3, sm: 4 } }}>
            <Box sx={{ textAlign: 'center' }}>
              <IconButton
                onClick={rejectCall}
                sx={{
                  width: { xs: 56, sm: 64, md: 70 },
                  height: { xs: 56, sm: 64, md: 70 },
                  backgroundColor: 'error.main',
                  color: '#fff',
                  '&:hover': { backgroundColor: 'error.dark' },
                }}
              >
                <CallEndIcon sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }} />
              </IconButton>
              <Typography variant="caption" display="block" mt={0.5} color="text.primary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                Decline
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'center' }}>
              <IconButton
                onClick={acceptCall}
                sx={{
                  width: { xs: 56, sm: 64, md: 70 },
                  height: { xs: 56, sm: 64, md: 70 },
                  backgroundColor: 'success.main',
                  color: '#fff',
                  '&:hover': { backgroundColor: 'success.dark' },
                }}
              >
                <CallIcon sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }} />
              </IconButton>
              <Typography variant="caption" display="block" mt={0.5} color="text.primary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                Accept
              </Typography>
            </Box>
          </Box>
        ) : (
          // Active call buttons - inline layout
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: 1, sm: 1.5, md: 2 } }}>
            {!isVideoCall && (
              <Box sx={{ textAlign: 'center' }}>
                <IconButton
                  onClick={toggleSpeaker}
                  sx={(theme) => ({
                    width: { xs: 44, sm: 48, md: 52 },
                    height: { xs: 44, sm: 48, md: 52 },
                    backgroundColor: isSpeaker ? theme.palette.action.selected : theme.palette.action.disabledBackground,
                    color: 'text.primary',
                    '&:hover': { backgroundColor: isSpeaker ? theme.palette.action.hover : theme.palette.action.disabled },
                  })}
                >
                  {isSpeaker ? <VolumeUpIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} /> : <VolumeOffIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />}
                </IconButton>
                <Typography variant="caption" display="block" mt={0.25} color="text.primary" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                  Speaker
                </Typography>
              </Box>
            )}

            <Box sx={{ textAlign: 'center' }}>
              <IconButton
                onClick={toggleMute}
                sx={(theme) => ({
                  width: { xs: 44, sm: 48, md: 52 },
                  height: { xs: 44, sm: 48, md: 52 },
                  backgroundColor: isMuted ? theme.palette.action.selected : theme.palette.action.disabledBackground,
                  color: 'text.primary',
                  '&:hover': { backgroundColor: isMuted ? theme.palette.action.hover : theme.palette.action.disabled },
                })}
              >
                {isMuted ? <MicOffIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} /> : <MicIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />}
              </IconButton>
              <Typography variant="caption" display="block" mt={0.25} color="text.primary" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                Mute
              </Typography>
            </Box>

            {isVideoCall && (
              <Box sx={{ textAlign: 'center' }}>
                <IconButton
                  onClick={toggleVideo}
                  sx={(theme) => ({
                    width: { xs: 44, sm: 48, md: 52 },
                    height: { xs: 44, sm: 48, md: 52 },
                    backgroundColor: !isVideoEnabled ? theme.palette.action.selected : theme.palette.action.disabledBackground,
                    color: 'text.primary',
                    '&:hover': { backgroundColor: !isVideoEnabled ? theme.palette.action.hover : theme.palette.action.disabled },
                  })}
                >
                  {isVideoEnabled ? <VideocamIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} /> : <VideocamOffIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />}
                </IconButton>
                <Typography variant="caption" display="block" mt={0.25} color="text.primary" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                  Video
                </Typography>
              </Box>
            )}

            <Box sx={{ textAlign: 'center' }}>
              <IconButton
                onClick={endCall}
                sx={{
                  width: { xs: 52, sm: 56, md: 60 },
                  height: { xs: 52, sm: 56, md: 60 },
                  backgroundColor: 'error.main',
                  color: '#fff',
                  '&:hover': { backgroundColor: 'error.dark' },
                }}
              >
                <CallEndIcon sx={{ fontSize: { xs: 22, sm: 26, md: 28 } }} />
              </IconButton>
              <Typography variant="caption" display="block" mt={0.25} color="text.primary" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                End
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Dialog open={endModal.open} onClose={closeEndModal} maxWidth="xs" fullWidth sx={{ zIndex: 3000 }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            {endModal.reason}
          </Typography>
          {endModal.details ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {endModal.details}
            </Typography>
          ) : null}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={closeEndModal} variant="contained">
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default CallScreen;
